import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Department,
  CreateDepartmentData,
  UpdateDepartmentData,
} from '../../types/organization.types';
import { useCreateDepartment, useTeamOptions, useUpdateDepartment } from '../../hooks/useOrgQueries';
import { LedgerDialog, LedgerField, LedgerInput, LedgerSelect, LedgerSelectOption } from '../ui';
import { cn } from '../../lib/cn';

const validationSchema = yup.object({
  name: yup
    .string()
    .default('')
    .required('Department name is required')
    .test('non-blank', 'Department name is required', (value) => Boolean(value && value.trim())),
  description: yup.string().defined().default(''),
  team: yup.string().default('').required('Team is required'),
  isActive: yup.boolean().defined().default(true),
});

type DepartmentFormValues = yup.InferType<typeof validationSchema>;

const defaultValues: DepartmentFormValues = {
  name: '',
  description: '',
  team: '',
  isActive: true,
};

interface DepartmentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  department?: Department | null;
}

export const DepartmentFormDialog: React.FC<DepartmentFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  department,
}) => {
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const teamsQuery = useTeamOptions(open && !department);
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const saving = createDepartment.isPending || updateDepartment.isPending;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DepartmentFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
    mode: 'onTouched',
  });

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    if (department) {
      reset({
        name: department.name,
        description: department.description || '',
        team: department.team,
        isActive: department.isActive,
      });
    } else {
      reset(defaultValues);
    }
  }, [department, open, reset]);

  const teamOptions: LedgerSelectOption[] = [
    { value: '', label: 'Select a team' },
    ...(teamsQuery.data ?? []).map((team) => ({
      value: team.id,
      label: `${team.name} (${team.companyName})`,
    })),
  ];

  // The disabled edit-mode query stays "pending" forever, so only create mode
  // may hold the submit button hostage on it.
  const optionsPending = !department && teamsQuery.isPending;

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const onSubmit = async (values: DepartmentFormValues) => {
    setError(null);
    try {
      if (department) {
        // Updates never move a department between teams, so no team here
        const updateData: UpdateDepartmentData = {
          name: values.name,
          description: values.description,
          isActive: values.isActive,
        };
        await updateDepartment.mutateAsync({ id: department.id, data: updateData });
      } else {
        const createData: CreateDepartmentData = {
          name: values.name,
          description: values.description,
          team: values.team,
        };
        await createDepartment.mutateAsync(createData);
      }

      onSuccess();
      handleClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || 'Failed to save department');
    }
  };

  const nameField = register('name');

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={department ? 'Edit Department' : 'Create New Department'}
      size="md"
      preventClose={saving}
      initialFocus={nameInputRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="department-form"
            disabled={saving || isSubmitting || optionsPending}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Saving…' : department ? 'Update' : 'Create'}
          </button>
        </>
      }
    >
      <form id="department-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {(error || teamsQuery.isError) && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error ?? 'Failed to load teams'}
          </p>
        )}

        <LedgerField label="Department Name" htmlFor="department-name" error={errors.name?.message}>
          <LedgerInput
            id="department-name"
            required
            invalid={!!errors.name}
            {...nameField}
            ref={(element) => {
              nameField.ref(element);
              nameInputRef.current = element;
            }}
          />
        </LedgerField>

        <LedgerField label="Description" htmlFor="department-description">
          <textarea
            id="department-description"
            rows={3}
            className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
            {...register('description')}
          />
        </LedgerField>

        {!department && (
          <LedgerField label="Team" error={errors.team?.message}>
            <Controller
              name="team"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Team"
                  value={field.value}
                  options={teamOptions}
                  onValueChange={field.onChange}
                  disabled={teamsQuery.isPending}
                />
              )}
            />
          </LedgerField>
        )}

        {department && (
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                role="switch"
                aria-checked={field.value}
                aria-label="Active"
                onClick={() => field.onChange(!field.value)}
                className="mt-1 flex cursor-pointer items-center gap-2"
              >
                <span aria-hidden className={cn('h-3 w-3 border border-ink', field.value && 'bg-ink')} />
                <span className="cap-label">{field.value ? 'Active' : 'Not active'}</span>
              </button>
            )}
          />
        )}
      </form>
    </LedgerDialog>
  );
};
